import { Button, Descriptions, Drawer, Tabs } from "antd";

import { EditOutlined } from "@ant-design/icons";

import type { PlanOfRecordMilestone } from "@/views/PlanOfRecord/planOfRecord.types";
import { AuditTimeline } from "@/components/PlanOfRecord/AuditTimeline";
import { ProvenanceBadge } from "@/components/PlanOfRecord/ProvenanceBadge";
import { ScopeBadge } from "@/components/PlanOfRecord/ScopeBadge";
import { StatusBadge } from "@/components/PlanOfRecord/StatusBadge";

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
    dateStyle: "medium",
  });
}

/**
 * P0-1 / P0-3 / P0-5 — a single milestone's full record: its values, where each came from,
 * every change ever made to it, and any unresolved disagreement with vendor-supplied data.
 */
export function MilestoneDrawer({
  milestone,
  onClose,
  canEdit,
  onEdit,
  requiredRoleName,
}: {
  milestone: PlanOfRecordMilestone | null;
  onClose: () => void;
  /** True only when the signed-in user holds `plan.edit`. */
  canEdit: boolean;
  onEdit: (milestone: PlanOfRecordMilestone) => void;
  requiredRoleName: string;
}) {
  return (
    <Drawer
      open={!!milestone}
      onClose={onClose}
      size={640}
      title={milestone?.label ?? ""}
      destroyOnHidden
      extra={
        canEdit && milestone ? (
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => onEdit(milestone)}
          >
            Edit
          </Button>
        ) : null
      }
    >
      {milestone ? (
        <Tabs
          items={[
            {
              key: "detail",
              label: "Detail",
              children: (
                <div className="flex flex-col gap-4">
                  <Descriptions column={1} size="small" bordered>
                    <Descriptions.Item label="Scope">
                      <span className="inline-flex items-center gap-2">
                        <ScopeBadge scope={milestone.scope} />
                        <span className="text-[#374151]">
                          {milestone.scopeLabel}
                        </span>
                      </span>
                    </Descriptions.Item>
                    <Descriptions.Item label="Created">
                      {new Date(milestone.createdAt).toLocaleString(undefined, {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}{" "}
                      <span className="text-[#8AA0A9]">
                        by {milestone.createdBy}
                      </span>
                    </Descriptions.Item>
                    <Descriptions.Item label="Originally planned">
                      {milestone.originalPlannedDate ? (
                        formatDate(milestone.originalPlannedDate)
                      ) : (
                        <span className="text-[#8AA0A9]">
                          Added after the plan was carried forward
                        </span>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Planned date">
                      {formatDate(milestone.plannedDate)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Actual date">
                      {formatDate(milestone.actualDate)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Status">
                      <StatusBadge status={milestone.status} />
                    </Descriptions.Item>
                    <Descriptions.Item label="At-risk threshold">
                      {milestone.thresholds ? (
                        `${milestone.thresholds.atRiskWithinDays} days before the planned date`
                      ) : (
                        <span className="text-[#8AA0A9]">
                          Not set — reads late once the planned date passes
                        </span>
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Provenance">
                      <ProvenanceBadge provenance={milestone.provenance} />
                    </Descriptions.Item>
                    <Descriptions.Item label="Rationale">
                      {milestone.rationale ?? (
                        <span className="text-[#8AA0A9]">None recorded</span>
                      )}
                    </Descriptions.Item>
                  </Descriptions>

                  {!canEdit ? (
                    <p className="rounded-[6px] bg-[#F0F2F4] p-3 text-[12px] leading-relaxed text-[#374151]">
                      You can read this plan and its history, but not change it.
                      Editing requires the{" "}
                      <span className="font-semibold">{requiredRoleName}</span>{" "}
                      role, granted through your organisation&rsquo;s identity
                      provider.
                    </p>
                  ) : null}
                </div>
              ),
            },
            {
              key: "history",
              label: `Change history (${milestone.audit.length})`,
              children: <AuditTimeline entries={milestone.audit} />,
            },
          ]}
        />
      ) : null}
    </Drawer>
  );
}
